import React, { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { 
  Clock, 
  AlertTriangle, 
  XCircle, 
  CheckCircle, 
  RefreshCw,
  ChevronRight,
  Timer,
  X
} from "lucide-react";
import { useTranslation } from 'react-i18next';
import { api } from "../../App";
import { toast } from "sonner";

export default function SLAAlertModal({ isOpen, onClose, onViewViolations, user }) {
  const { t } = useTranslation();
  const [slaStats, setSlaStats] = useState(null);
  const [loading, setLoading] = useState(true);

  // Determine user's department from designation
  const getUserDepartment = () => {
    const revenueDesignations = ["tahsildar", "deputy_tahsildar", "shirestedar", "revenue_inspector", "village_accountant", "first_division_clerk", "second_division_clerk"];
    const surveyDesignations = ["ddlr", "adlr", "surveyor", "survey_inspector", "asst_surveyor"];
    
    if (user?.designation) {
      if (revenueDesignations.includes(user.designation)) return "revenue";
      if (surveyDesignations.includes(user.designation)) return "survey";
    }
    return user?.department || null;
  };

  const userDepartment = getUserDepartment();

  const fetchSLAStats = async () => {
    try {
      const deptParam = userDepartment ? `?department=${userDepartment}` : '';
      const response = await api.get(`/sla/dashboard${deptParam}`);
      setSlaStats(response.data);
    } catch (error) {
      console.error('Failed to fetch SLA stats:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen) {
      fetchSLAStats();
    }
  }, [isOpen]);

  const handleViewViolations = () => {
    onClose();
    if (onViewViolations) {
      onViewViolations();
    }
  };

  const hasIssues = slaStats && (slaStats.warning > 0 || slaStats.violated > 0);

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent 
        className={`sm:max-w-md ${hasIssues ? 'border-amber-300' : 'border-emerald-300'}`}
        data-testid="sla-alert-modal"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <div className={`p-2 rounded-lg ${hasIssues ? 'bg-amber-100' : 'bg-emerald-100'}`}>
              <Timer className={`w-5 h-5 ${hasIssues ? 'text-amber-700' : 'text-emerald-700'}`} />
            </div>
            <span>{t('sla.dashboard')}</span>
          </DialogTitle>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-600"></div>
          </div>
        ) : slaStats ? (
          <div className="space-y-4">
            {/* Stats Summary */}
            <div className="text-sm text-slate-600 text-center">
              {t('sla.totalWithSla')}: <span className="font-semibold">{slaStats.total_with_sla}</span>
            </div>

            {/* Stats Grid */}
            <div className="grid grid-cols-3 gap-3">
              {/* On Track */}
              <div className="bg-emerald-50 rounded-lg p-4 text-center border border-emerald-200" data-testid="sla-modal-ontrack">
                <CheckCircle className="w-6 h-6 text-emerald-600 mx-auto mb-2" />
                <p className="text-3xl font-bold text-emerald-700">{slaStats.on_track}</p>
                <p className="text-xs text-emerald-600 mt-1">{t('sla.onTrack')}</p>
              </div>
              
              {/* Warning */}
              <div className="bg-amber-50 rounded-lg p-4 text-center border border-amber-200" data-testid="sla-modal-warning">
                <AlertTriangle className="w-6 h-6 text-amber-600 mx-auto mb-2" />
                <p className="text-3xl font-bold text-amber-700">{slaStats.warning}</p>
                <p className="text-xs text-amber-600 mt-1">{t('sla.warningShort') || 'Warning'}</p>
              </div>
              
              {/* Violated */}
              <div className="bg-red-50 rounded-lg p-4 text-center border border-red-200" data-testid="sla-modal-violated">
                <XCircle className="w-6 h-6 text-red-600 mx-auto mb-2" />
                <p className="text-3xl font-bold text-red-700">{slaStats.violated}</p>
                <p className="text-xs text-red-600 mt-1">{t('sla.violations')}</p>
              </div>
            </div>

            {/* Alert Message */}
            {hasIssues ? (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-center">
                <p className="text-sm text-gray-900 font-semibold">
                  {t('sla.modalMessagePart1') || 'Please view '} 
                  <span className="text-blue-600">{t('sla.modalMessageHighlight') || 'Applications Deadline Issues'}</span>
                  {t('sla.modalMessagePart2') || ' tab for applications approaching the process deadline.'}
                </p>
              </div>
            ) : (
              <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 text-center">
                <p className="text-sm text-emerald-800">
                  {t('sla.allOnTrack') || 'All applications are being processed on time'}
                </p>
              </div>
            )}

            {/* Violations by Designation */}
            {slaStats.violated > 0 && slaStats.violations_by_designation && Object.keys(slaStats.violations_by_designation).length > 0 && (
              <div className="bg-red-50/50 rounded-lg p-3">
                <p className="text-xs font-medium text-red-800 mb-2">{t('sla.violationsByRole') || 'Violations by Role'}:</p>
                <div className="flex flex-wrap gap-2">
                  {Object.entries(slaStats.violations_by_designation).map(([designation, count]) => {
                    // Handle null/undefined designation
                    const displayDesignation = (!designation || designation === 'null' || designation === 'undefined')
                      ? (t('sla.unassigned') || 'Unassigned')
                      : (t(`designation.${designation}`) || designation);
                    
                    return (
                      <span 
                        key={designation || 'unassigned'}
                        className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-red-100 text-red-800"
                      >
                        {displayDesignation}: {count}
                      </span>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex gap-3 pt-2">
              <Button
                variant="outline"
                onClick={onClose}
                className="flex-1"
                data-testid="sla-modal-dismiss"
              >
                {t('common.close') || 'Close'}
              </Button>
            </div>
          </div>
        ) : null}
      </DialogContent>
    </Dialog>
  );
}
